import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, MapPin, LocateFixed, Loader2, Briefcase, UserCircle2, SlidersHorizontal, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PresenceAvatar from "@/components/PresenceAvatar";
import WorkResultsMap from "@/pages/work/WorkResultsMap";
import { List, Map as MapIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AREAS = ["Tecnologia", "Design", "Marketing", "Vendas", "Administrativo", "Construção", "Saúde", "Educação", "Logística", "Alimentação", "Beleza", "Serviços gerais", "Outros"];
const RADII = [5, 10, 25, 50, 100];

export default function WorkSearchBar({ onJobSelect }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ q: "", city: "", neighborhood: "", area: "", radius_km: "" });
  const [pos, setPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list");
  const timer = useRef(null);
  const reqId = useRef(0);

  const active = f.q.trim() || f.city.trim() || f.neighborhood.trim() || f.area || (f.radius_km && pos);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!active) { setRes(null); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      const id = ++reqId.current;
      try {
        const params = { q: f.q.trim() || undefined, city: f.city.trim() || undefined, neighborhood: f.neighborhood.trim() || undefined, area: f.area || undefined };
        if (f.radius_km && pos) { params.lat = pos.lat; params.lng = pos.lng; params.radius_km = f.radius_km; }
        else if (pos) { params.lat = pos.lat; params.lng = pos.lng; }
        const r = await axios.get(`${API}/work/search`, { params });
        if (id === reqId.current) setRes(r.data);
      } catch { if (id === reqId.current) setRes({ jobs: [], freelancers: [], total: 0 }); }
      if (id === reqId.current) setLoading(false);
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [f, pos]); // eslint-disable-line react-hooks/exhaustive-deps

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocalização não suportada");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); setF(x => ({ ...x, radius_km: x.radius_km || 25 })); setLocating(false); toast.success("Localização obtida"); },
      () => { setLocating(false); toast.error("Permita o acesso à localização"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  const clear = () => { setF({ q: "", city: "", neighborhood: "", area: "", radius_km: "" }); setRes(null); };

  return (
    <Card className="p-3 space-y-3" data-testid="work-search-card">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="work-search-input" className="pl-9 rounded-full" placeholder="Buscar vagas e freelancers (ex.: eletricista, designer, garçom...)" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        </div>
        <Button variant={open ? "secondary" : "outline"} className="rounded-full gap-1" data-testid="work-search-filters-toggle" onClick={() => setOpen(o => !o)}><SlidersHorizontal className="w-4 h-4" /> <span className="hidden sm:inline">Filtros</span></Button>
        {active && <Button variant="ghost" size="icon" className="rounded-full" data-testid="work-search-clear" onClick={clear} aria-label="Limpar"><X className="w-4 h-4" /></Button>}
      </div>
      {open && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="work-search-filters">
          <Input data-testid="work-filter-city" placeholder="Cidade" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          <Input data-testid="work-filter-neighborhood" placeholder="Bairro" value={f.neighborhood} onChange={(e) => setF({ ...f, neighborhood: e.target.value })} />
          <select data-testid="work-filter-area" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Profissão / área</option>
            {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="flex gap-1">
            <select data-testid="work-filter-radius" value={f.radius_km} onChange={(e) => setF({ ...f, radius_km: e.target.value ? Number(e.target.value) : "" })} className="h-10 flex-1 rounded-md border border-input bg-background px-3 text-sm" disabled={!pos}>
              <option value="">{pos ? "Raio (km)" : "Ative a localização"}</option>
              {RADII.map(r => <option key={r} value={r}>até {r} km</option>)}
            </select>
            <Button type="button" variant={pos ? "secondary" : "outline"} size="icon" data-testid="work-locate-btn" onClick={locate} disabled={locating} aria-label="Usar minha localização" title="Usar minha localização">
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className={`w-4 h-4 ${pos ? "text-emerald-600" : ""}`} />}
            </Button>
          </div>
        </div>
      )}
      {loading && <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="work-search-loading"><Loader2 className="w-3 h-3 animate-spin" /> Buscando...</div>}
      {res && !loading && (
        <div className="space-y-3" data-testid="work-search-results">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground" data-testid="work-search-count">{res.total} resultado(s){f.radius_km && pos ? ` · até ${f.radius_km} km de você` : ""}</div>
            <div className="flex rounded-full border p-0.5" data-testid="work-view-toggle">
              <button type="button" data-testid="work-view-list" onClick={() => setView("list")} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><List className="w-3.5 h-3.5" /> Lista</button>
              <button type="button" data-testid="work-view-map" onClick={() => setView("map")} className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${view === "map" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><MapIcon className="w-3.5 h-3.5" /> Mapa</button>
            </div>
          </div>
          {view === "map" && <WorkResultsMap jobs={res.jobs} freelancers={res.freelancers} center={pos} radiusKm={f.radius_km && pos ? Number(f.radius_km) : null} onJobSelect={onJobSelect} />}
          {res.total === 0 && <div className="text-sm text-muted-foreground py-3 text-center" data-testid="work-search-empty">Nada encontrado com esses filtros. Tente ampliar o raio ou remover o bairro.</div>}
          {view === "list" && res.jobs.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><Briefcase className="w-3 h-3" /> Vagas ({res.jobs.length})</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {res.jobs.map(j => (
                  <button key={j.job_id} type="button" data-testid={`work-result-job-${j.job_id}`} onClick={() => onJobSelect?.(j)} className="text-left p-3 rounded-xl border hover:border-primary/60 hover:bg-muted/40 transition-colors">
                    <div className="text-sm font-semibold truncate">{j.title}</div>
                    <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2 mt-0.5">
                      <span>{j.category}</span>
                      {(j.location || j.city) && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{j.location || j.city}{j.neighborhood ? ` · ${j.neighborhood}` : ""}</span>}
                      {j.remote && <span>Remoto</span>}
                      {j.distance_km != null && <span className="text-emerald-600 font-semibold">{j.distance_km} km</span>}
                    </div>
                    <div className="text-xs font-bold text-primary mt-1">R$ {Number(j.budget || 0).toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {view === "list" && res.freelancers.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><UserCircle2 className="w-3 h-3" /> Freelancers ({res.freelancers.length})</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {res.freelancers.map(p => (
                  <Link key={p.user_id} to={`/user/${p.user_id}`} data-testid={`work-result-freelancer-${p.user_id}`} className="flex items-center gap-3 p-3 rounded-xl border hover:border-primary/60 hover:bg-muted/40 transition-colors">
                    <PresenceAvatar src={p.picture} name={p.name} online={p.online} className="w-10 h-10" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{p.headline || (p.skills || []).slice(0, 3).join(", ")}</div>
                      <div className="text-[11px] text-muted-foreground flex gap-2">
                        {p.city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.city}{p.neighborhood ? ` · ${p.neighborhood}` : ""}</span>}
                        {p.distance_km != null && <span className="text-emerald-600 font-semibold">{p.distance_km} km</span>}
                      </div>
                    </div>
                    {p.available && <Badge variant="secondary" className="text-[10px]">Disponível</Badge>}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
